/* Аватарки «Озвучки» собираются из пикселей. Строки списка, лица подборок, герой карточки голоса и персонажа, портреты персонажей и актёров
   приходят позже текста строки: раньше место стояло пустым, а картинка потом возникала разом. Теперь, пока картинка едет, место — заглушка
   скелетона (.pxw, pixel-reveal.css), а пришедшая картинка проявляется крупными пикселями, которые за ~0,4 с мельчают до чёткой картинки
   (холст поверх места), и холст уступает место самой картинке.

   Модуль сам находит места в разметке (наблюдатель за документом): фон с url(…) или <img> внутри «Озвучки» (#vp), панели (#side) и каталога
   персонажей, а также лица рядов «Кто говорит» / «Кто в кадре» Студии (.ps-fi) на любом экране. Разметка списков перерисовывается часто — картинка, уже пришедшая однажды (или лежащая в кэше браузера), встаёт сразу, без сборки.
   Рисуется только показ: холст с чужой картинкой «испачкан», но его пиксели никто не читает. «Меньше движения» и заморозка .still — без сборки. */
import { reduced } from '../../ui/motion';
import './pixel-reveal.css';

/* «Говорящий кружок»: большой портрет выбранного персонажа (#apHero, кольцо #apRing) и миниатюры видов в «Ещё настройки» (#apSet) */
const ROOTS = '#vp, #side, #apHero, #apSet';
const BG = '[style*="url("]';
const IMG = '.cv > img, .icv > img';
/** Лица рядов «Кто говорит» / «Кто в кадре» Студии (.ps-fi, bot-people.css и PeoplePicker) — на любом экране; картинки кадров слайдера
    главной «Студии» (веер образов, кружок, голоса — .st-hs .rt) и обложки «Шаблонов и подборок» (.st-mas: стопка .deck — обложка .pic и карты
    под ней): приходили с задержкой и возникали разом */
/* размытый фон плитки (img.bg) не собирается: он под обложкой и сам по себе ничего не показывает */
const FACE = '.ps-fi > img, #studio .st-hs .rt img, #studio .st-mas :is(.pic, .deck > i) > img:not(.bg)';
/** Кружок экрана заказа (.ar-pic: персонаж или снимок того, кто говорит, пока заказ идёт — размыт): пиксели под размытием не читаются,
    поэтому здесь не сборка, а заглушка-пульс и мягкое проявление (pixel-reveal.css, .pxs) */
const SOFT = '.ar-pic [style*="url("]';
/** Картинка не пришла: ещё попытки с новым адресом (сбой сети WebView), пауза растёт */
const RETRIES = 2;
const bust = (url: string, n: number) => url + (url.includes('?') ? '&' : '?') + 'r=' + n;
/** Пришедшие картинки (адрес): повторная отрисовка строки ставит их сразу */
const LOADED = new Set<string>();
/** Идущие сборки (адрес → картинка и начало): строку перерисовали посреди сборки («Рекомендуем» перерисовывается, пока доезжают
    подборки и рекомендации) — новое место продолжает ту же сборку с того же мгновения, а не встаёт готовой картинкой */
const RUNNING = new Map<string, Run>();
/** Сборка: картинка и начало (t0 ставится, когда картинка раскодирована — до этого сборка не идёт) */
type Run = { img: HTMLImageElement; t0: number; ready?: boolean };
/** Сборка: шаги размера пикселя от крупного к мелкому, мс */
const DURATION = 420;

const urlOf = (el: HTMLElement): string | null => {
  if (el instanceof HTMLImageElement) return el.currentSrc || el.src || null;
  const m = /url\(\s*["']?([^"')]+)["']?\s*\)/.exec(el.style.backgroundImage || el.style.background || '');
  return m ? m[1] : null;
};

/** Картинка внутри места с подрезкой как у фона cover / object-fit cover и его позицией */
function drawCover(ctx: CanvasRenderingContext2D, img: HTMLImageElement, w: number, h: number, px: number, py: number) {
  const k = Math.max(w / img.naturalWidth, h / img.naturalHeight),
    dw = img.naturalWidth * k,
    dh = img.naturalHeight * k;
  ctx.drawImage(img, (w - dw) * px, (h - dh) * py, dw, dh);
}

const pos = (v: string, i: number) => {
  const part = (v || '50% 50%').split(/\s+/)[i] ?? '50%';
  return part.endsWith('%')
    ? parseFloat(part) / 100
    : part === 'top' || part === 'left'
      ? 0
      : part === 'bottom' || part === 'right'
        ? 1
        : 0.5;
};

/** Скруглённый прямоугольник контуром: roundRect холста есть не во всех WebView Telegram (iOS до 16, старый Android) */
function shape(ctx: CanvasRenderingContext2D, w: number, h: number, r: number) {
  const k = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(k, 0);
  ctx.arcTo(w, 0, w, h, k);
  ctx.arcTo(w, h, 0, h, k);
  ctx.arcTo(0, h, 0, 0, k);
  ctx.arcTo(0, 0, w, 0, k);
  ctx.closePath();
}

/** Обмер места для сборки (только чтение: размеры, стили, смещение) — null, если сборка не нужна */
function measure(el: HTMLElement) {
  const isImg = el instanceof HTMLImageElement,
    host = isImg ? el.parentElement : el;
  const w = el.offsetWidth,
    h = el.offsetHeight;
  if (!host || w < 8 || h < 8) return null;
  const cs = getComputedStyle(el),
    size = isImg ? cs.objectFit : cs.backgroundSize,
    position = isImg ? cs.objectPosition : `${cs.backgroundPositionX} ${cs.backgroundPositionY}`;
  if (size !== 'cover') return null;
  /* место за краем экрана или спрятано — картинка встаёт сразу, без сборки */
  const r = el.getBoundingClientRect();
  if (!(r.bottom > 0 && r.top < innerHeight && r.width > 0) || cs.visibility === 'hidden') return null;
  const hs = getComputedStyle(host);
  /* заглушка — фон самого места (у <img> — рамки .cv): её цвет — первый кадр сборки, её форма собирается вместе с картинкой */
  const shell = isImg ? host : el,
    skin = getComputedStyle(shell).backgroundColor || 'rgba(128,128,128,.2)';
  const rad = (v: string, side: number) => (v.endsWith('%') ? (parseFloat(v) / 100) * side : parseFloat(v) || 0);
  /* у <img> круг часто даёт не она сама, а рамка с подрезкой (.cv обложки подборки, .ps-fi лица: radius 50%, overflow hidden, у картинки 0) —
     подрезку сборка снимает, поэтому форму берём у рамки, иначе собирался бы квадрат и в конце резко становился кругом */
  const clip = isImg && hs.overflow !== 'visible' ? rad(hs.borderTopLeftRadius, host.offsetWidth) : 0;
  const radius = Math.min(Math.max(rad(cs.borderTopLeftRadius, w), clip), Math.min(w, h) / 2);
  /* место <img> внутри рамки: offsetLeft/Top считаются от offsetParent — у рамки без position (обложка .cv карточки подборки) это уже
     карточка, и холст уезжал за край карточки; тогда вычитаем смещение самой рамки от того же предка */
  const box = !isImg
    ? { left: 0, top: 0 }
    : el.offsetParent === host
      ? { left: el.offsetLeft, top: el.offsetTop }
      : { left: el.offsetLeft - host.offsetLeft - host.clientLeft, top: el.offsetTop - host.offsetTop - host.clientTop };
  return {
    host,
    shell,
    skin,
    w,
    h,
    box,
    radius,
    position,
    static: hs.position === 'static',
    /* подрезку снимаем только у круга (аватарки): его пиксельный край собирается вместе с картинкой. У плитки со скруглением (обложки «Всех шаблонов»)
       подрезка остаётся — иначе размытый фон и углы картинки выходили за скругление, и плитка «закруглялась» только в конце сборки */
    unclip: clip >= Math.min(host.offsetWidth, host.offsetHeight) / 2 - 1,
  };
}
type Plan = NonNullable<ReturnType<typeof measure>>;

/* Пришедшие картинки собираются пачкой раз в кадр: сначала обмер всех мест, потом вставка холстов. Раньше каждая картинка в своём onload
   читала размеры и стили и тут же вставляла холст — следующая картинка читала уже «грязную» раскладку, и браузер раскладывал список заново
   на каждую (замер «Озвучки»: ~10 мс на картинку, до 70 мс в одном кадре на ПК) */
const QUEUE: { el: HTMLElement; img: HTMLImageElement; done: () => void; run: Run }[] = [];
let qf = 0;
/* картинка раскодируется заранее (img.decode — вне главного потока): иначе первый drawImage сборки раскодировал её прямо в кадре */
function assemble(el: HTMLElement, img: HTMLImageElement, done: () => void, run: Run = { img, t0: 0 }) {
  const go = () => {
    if (!run.ready) {
      run.ready = true;
      run.t0 = performance.now();
    }
    QUEUE.push({ el, img, done, run });
    if (!qf) qf = requestAnimationFrame(flush);
  };
  if (run.ready || typeof img.decode !== 'function') return go();
  img.decode().then(go, go);
}
function flush() {
  qf = 0;
  const L = QUEUE.splice(0);
  const plans = L.map(q => {
    if (!q.el.isConnected) return null;
    try {
      return measure(q.el);
    } catch {
      return null;
    }
  });
  L.forEach((q, i) => {
    const plan = plans[i];
    if (!plan) return q.done();
    try {
      mount(q.img, q.done, q.run.t0, plan);
    } catch {
      q.done();
    }
  });
}

/** Собрать картинку на холсте поверх места host (для <img> — поверх его рамки), затем отдать место картинке. Только запись — обмер уже в plan */
function mount(img: HTMLImageElement, done: () => void, t0: number, plan: Plan) {
  const { host, shell, skin, w, h, box, radius, position } = plan;
  const dpr = Math.min(devicePixelRatio || 1, 2),
    cv = document.createElement('canvas');
  cv.width = Math.round(w * dpr);
  cv.height = Math.round(h * dpr);
  cv.className = 'pxr';
  cv.setAttribute('aria-hidden', 'true');
  /* холст без скругления и подрезки места: край круга — тоже пиксели, он собирается вместе с картинкой */
  cv.style.cssText = `left:${box.left}px;top:${box.top}px;width:${w}px;height:${h}px`;
  /* холст — над фоном места, под его содержимым (кнопка ▶ строки): свой контекст наложения и z-index -1 */
  const restore = { position: host.style.position, isolation: host.style.isolation, overflow: host.style.overflow };
  if (plan.static) host.style.position = 'relative';
  host.style.isolation = 'isolate';
  if (plan.unclip) host.style.overflow = 'visible';
  /* гладкая заглушка под холстом гаснет: место рисует только холст */
  shell.classList.add('pxa');
  host.prepend(cv);
  const ctx = cv.getContext('2d'),
    tiny = document.createElement('canvas'),
    tctx = tiny.getContext('2d');
  if (!ctx || !tctx) {
    cv.remove();
    return done();
  }
  const px = pos(position, 0),
    py = pos(position, 1),
    W = cv.width,
    H = cv.height;
  /* картинка, уже подрезанная по месту (cover) в размер холста — один раз: каждый кадр ужимал исходную картинку (обложки — до 1080 точек)
     в сетку клеток заново, и кадр сборки стоил ~24 мс на ПК; теперь кадр берёт её из маленького холста */
  const fit = document.createElement('canvas');
  fit.width = W;
  fit.height = H;
  const fctx = fit.getContext('2d');
  if (!fctx) {
    cv.remove();
    return done();
  }
  fctx.imageSmoothingEnabled = true;
  fctx.imageSmoothingQuality = 'high';
  drawCover(fctx, img, W, H, px, py);
  let over = false;
  const finish = () => {
    if (over) return;
    over = true;
    /* подрезка рамки — до показа картинки: квадратная <img> в рамке без подрезки на кадр вставала квадратом поверх готового круга */
    host.style.overflow = restore.overflow;
    done();
    /* картинка уже стоит на месте — холст снимается следующим кадром, без мигания между ними */
    let gone = false;
    const off = () => {
      if (gone) return;
      gone = true;
      cv.remove();
      host.style.position = restore.position;
      host.style.isolation = restore.isolation;
      shell.classList.remove('pxa');
    };
    requestAnimationFrame(off);
    /* кадров нет — снимаем таймером */
    setTimeout(off, 100);
  };
  /* кадры не идут (вкладка или WebView в фоне) — картинка встаёт сама, холст не застревает над местом */
  setTimeout(finish, Math.max(0, t0 + DURATION - performance.now()) + 600);
  /* любой сбой кадра (старый WebView, нулевой размер) — сразу готовая картинка, а не застывший пустой круг */
  const frame = (now: number) => {
    try {
      step(now);
    } catch {
      finish();
    }
  };
  const step = (now: number) => {
    if (over) return;
    if (!cv.isConnected) return finish();
    /* отметка первого кадра бывает чуть раньше t0 — доля не уходит ниже нуля (иначе степень дробная от отрицательного — NaN и холст 0×0) */
    const t = Math.max(0, Math.min(1, (now - t0) / DURATION));
    if (t >= 1) {
      ctx.imageSmoothingEnabled = true;
      ctx.clearRect(0, 0, W, H);
      ctx.save();
      shape(ctx, W, H, radius * dpr);
      ctx.clip();
      ctx.drawImage(fit, 0, 0);
      ctx.restore();
      return finish();
    }
    /* клеток по ширине: от 5 до ширины в точках, по экспоненте — крупные шаги в начале, мелкие к концу */
    const cells = Math.max(5, Math.round(5 * Math.pow(W / 5, Math.pow(t, 1.6))));
    const rows = Math.max(1, Math.round((cells * H) / W));
    tiny.width = cells;
    tiny.height = rows;
    tctx.imageSmoothingEnabled = true;
    tctx.globalCompositeOperation = 'source-over';
    /* кадр: цвет заглушки, поверх — картинка, проступающая за первую половину сборки */
    tctx.globalAlpha = 1;
    tctx.fillStyle = skin;
    tctx.fillRect(0, 0, cells, rows);
    tctx.globalAlpha = Math.min(1, t / 0.45);
    tctx.drawImage(fit, 0, 0, cells, rows);
    /* форма места (круг, скруглённый прямоугольник) на той же сетке клеток: край тоже пиксельный и мельчает вместе с картинкой */
    tctx.globalAlpha = 1;
    tctx.globalCompositeOperation = 'destination-in';
    shape(tctx, cells, rows, (radius * cells) / w);
    tctx.fill();
    ctx.imageSmoothingEnabled = false;
    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, W, H);
    ctx.drawImage(tiny, 0, 0, W, H);
    /* крупные клетки края выступают за настоящий круг: место казалось больше и в конце «ужималось» — режем кадр по готовой форме,
       ступенчатый край собирается внутри неё и не выходит за неё */
    ctx.globalCompositeOperation = 'destination-in';
    shape(ctx, W, H, radius * dpr);
    ctx.fill();
    ctx.globalCompositeOperation = 'source-over';
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);
}

function watch(el: HTMLElement) {
  if (el.dataset.pxr) return;
  const url = urlOf(el);
  if (!url || url.startsWith('data:')) return;
  if (LOADED.has(url)) {
    const run = RUNNING.get(url);
    if (!run || performance.now() - run.t0 >= DURATION || el.dataset.pxr || reduced()) return;
    /* та же сборка на новом месте: картинка прячется, пока холст не дорисует */
    el.dataset.pxr = '1';
    el.classList.add('pxw');
    assemble(el, run.img, () => el.classList.remove('pxw'), run);
    return;
  }
  el.dataset.pxr = '1';
  /* ленивая <img> (обложки шаблонов): своя загрузка не нужна и сломала бы ленивость — ждём её саму и собираем из неё */
  if (el instanceof HTMLImageElement && el.loading === 'lazy') {
    if (el.complete && el.naturalWidth) {
      LOADED.add(url);
      return;
    }
    el.classList.add('pxw');
    const show = () => el.classList.remove('pxw');
    el.addEventListener(
      'load',
      () => {
        LOADED.add(url);
        if (reduced() || !el.isConnected) return show();
        assemble(el, el, show);
      },
      { once: true },
    );
    el.addEventListener('error', show, { once: true });
    return;
  }
  /* своя загрузка — только у места на экране (или рядом): длинные списки («Подборки», «Персонажи» — сотни строк) раньше качали и раскодировали
     все картинки сразу, а обмер мест за краем экрана (в том числе строк с content-visibility) раскладывал каждую строку отдельно — 642 мс
     на смену сегмента «Озвучки» на ПК. Место за краем ждёт наблюдателя; браузер сам покажет его картинку, если она придёт раньше */
  if (NEAR) {
    NEAR.observe(el);
    return;
  }
  load(el, url);
}

/** Место подошло к экрану: его картинка уже пришла (кэш, загрузка самого браузера) — встаёт сама; нет — загрузка и сборка */
const NEAR =
  typeof IntersectionObserver === 'undefined'
    ? null
    : new IntersectionObserver(
        entries =>
          entries.forEach(e => {
            if (!e.isIntersecting) return;
            const el = e.target as HTMLElement;
            NEAR!.unobserve(el);
            const url = el.isConnected ? urlOf(el) : null;
            if (!url || url.startsWith('data:') || LOADED.has(url)) return;
            load(el, url);
          }),
        { rootMargin: '200px 0px' },
      );

function load(el: HTMLElement, url: string) {
  const img = new Image();
  img.decoding = 'async';
  /* лица Студии могут прийти с чужого адреса без CORS — холсту это не мешает: его пиксели никто не читает */
  img.src = url;
  let tries = 0;
  /* в кэше браузера — сразу, как раньше */
  if (img.complete && img.naturalWidth) {
    LOADED.add(url);
    return;
  }
  el.classList.add('pxw');
  const show = () => el.classList.remove('pxw');
  img.onload = () => {
    LOADED.add(url);
    LOADED.add(img.src);
    if (!el.isConnected || urlOf(el) !== url) return show();
    /* пришла повторная попытка: сама <img> со старым адресом уже сломана — ставим ей рабочий */
    if (tries && el instanceof HTMLImageElement) el.src = img.src;
    if (el.matches(SOFT)) {
      /* проявление — переходом (.pxs): снять заглушку в следующем кадре, чтобы прозрачность успела встать */
      el.classList.add('pxs');
      return requestAnimationFrame(show);
    }
    /* видно ли место, решает обмер пачки (measure): чтение раскладки здесь, в onload каждой картинки, и было главным тормозом */
    if (reduced()) return show();
    const run: Run = { img, t0: performance.now() };
    RUNNING.set(url, run);
    setTimeout(() => RUNNING.get(url) === run && RUNNING.delete(url), DURATION + 100);
    assemble(el, img, show, run);
  };
  /* картинка не пришла — ещё попытки; не пришла совсем — сломанная <img> прячется (не белое пятно WebView), у лица — буква имени */
  img.onerror = () => {
    if (tries < RETRIES && el.isConnected && urlOf(el) === url) {
      tries += 1;
      setTimeout(() => {
        img.src = bust(url, tries);
      }, 700 * tries);
      return;
    }
    show();
    if (!(el instanceof HTMLImageElement) || !el.isConnected) return;
    el.classList.add('pxf');
    const fc = el.closest<HTMLElement>('.ps-fc'),
      name = fc?.querySelector('b')?.textContent || fc?.getAttribute('aria-label') || '';
    if (el.parentElement) el.parentElement.dataset.ini = Array.from(name.trim())[0] ?? '·';
  };
}

function scan(root: ParentNode) {
  root.querySelectorAll<HTMLElement>(`:is(${ROOTS}) ${BG}, :is(${ROOTS}) :is(${IMG}), ${FACE}, ${SOFT}`).forEach(watch);
}

let installed = false;
/** Включить сборку аватарок; повторный вызов ничего не делает */
export function installPixelReveal() {
  if (installed || typeof MutationObserver === 'undefined' || typeof document === 'undefined') return;
  installed = true;
  new MutationObserver(records => {
    for (const r of records)
      r.addedNodes.forEach(n => {
        if (!(n instanceof HTMLElement)) return;
        if ((n.closest(ROOTS) && (n.matches(BG) || n.matches(IMG))) || n.matches(FACE) || n.matches(SOFT)) watch(n);
        scan(n);
      });
  }).observe(document.documentElement, { childList: true, subtree: true });
  scan(document);
}

installPixelReveal();
